import React, { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../hooks/useUserRole';
import { useAlertCounts } from '../../hooks/useAlertCounts';
import { useAIGenerationEnabled } from '../../hooks/useAIGenerationEnabled';
import { AlertBadge } from './AlertBadge';
import { AdminAIWarningBanner } from './AdminAIWarningBanner';
import {
  LayoutDashboard,
  Users,
  Mail,
  Brain,
  Settings,
  LogOut,
  Send,
  FileText,
  Bug,
  Activity,
  HardDrive,
  Database,
  Euro,
  UserCog,
  Megaphone,
  Webhook,
  AlertTriangle,
  Share2,
  MessageSquare,
  BookOpen,
  UserPlus,
  Code2,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  UserMinus,
  Compass,
  MessageCircle,
  HelpCircle
} from 'lucide-react';

interface AdminLayoutProps {
  children: ReactNode;
}

const SIDEBAR_COLLAPSED_KEY = 'admin_sidebar_collapsed';
const SIDEBAR_OPEN_GROUPS_KEY = 'admin_sidebar_open_groups';

const SEVERITY_RANK: Record<string, number> = { critical: 3, warning: 2, info: 1 };

export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, isSuperAdmin } = useAuth();
  const { role, isSupportUser } = useUserRole();
  const { alertCounts } = useAlertCounts();
  const { isEnabled: aiEnabled } = useAIGenerationEnabled();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_OPEN_GROUPS_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return { Klanten: true };
  });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  const toggleGroup = (label: string) => {
    setOpenGroups(prev => {
      const next = { ...prev, [label]: !prev[label] };
      try {
        localStorage.setItem(SIDEBAR_OPEN_GROUPS_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const handleLogout = async () => {
    await signOut();
    if (isSupportUser) {
      navigate('/support');
    } else {
      navigate('/admin');
    }
  };

  const navGroups: Array<{
    label: string | null;
    items: Array<{
      label: string;
      path: string;
      icon: typeof LayoutDashboard;
      section: string | null;
      superAdminOnly: boolean;
      supportAllowed: boolean;
    }>;
  }> = [
    {
      label: null,
      items: [
        { label: 'Home', path: '/admin/dashboard', icon: LayoutDashboard, section: null, superAdminOnly: false, supportAllowed: false },
        { label: 'Handleiding', path: isSupportUser ? '/support/handleiding' : '/admin/handleiding', icon: HelpCircle, section: null, superAdminOnly: false, supportAllowed: true },
      ],
    },
    {
      label: 'Klanten',
      items: [
        { label: 'Gebruikers', path: isSupportUser ? '/support/users' : '/admin/users', icon: Users, section: null, superAdminOnly: false, supportAllowed: true },
        { label: 'Klanttraject', path: isSupportUser ? '/support/klanttraject' : '/admin/klanttraject', icon: Compass, section: null, superAdminOnly: false, supportAllowed: true },
        { label: 'Signups', path: '/admin/signups', icon: UserPlus, section: 'signups', superAdminOnly: false, supportAllowed: false },
        { label: 'Opzeggingen', path: '/admin/cancellations', icon: UserMinus, section: null, superAdminOnly: false, supportAllowed: false },
        { label: 'Financieel', path: '/admin/financial', icon: Euro, section: null, superAdminOnly: false, supportAllowed: false },
      ],
    },
    {
      label: 'Communicatie',
      items: [
        { label: 'Campaigns', path: '/admin/campaigns', icon: Mail, section: 'campaigns', superAdminOnly: false, supportAllowed: false },
        { label: 'Broadcast', path: '/admin/broadcast', icon: Megaphone, section: null, superAdminOnly: true, supportAllowed: false },
        { label: 'Social Media', path: '/admin/social-media', icon: Share2, section: 'social_media', superAdminOnly: false, supportAllowed: false },
        { label: 'WhatsApp', path: isSupportUser ? '/support/whatsapp' : '/admin/whatsapp', icon: MessageCircle, section: null, superAdminOnly: false, supportAllowed: true },
      ],
    },
    {
      label: 'Content',
      items: [
        { label: 'Templates', path: '/admin/templates', icon: FileText, section: null, superAdminOnly: false, supportAllowed: false },
        { label: 'Kennisbank', path: '/admin/kennisbank', icon: BookOpen, section: null, superAdminOnly: false, supportAllowed: false },
        { label: 'Chatbot', path: '/admin/chatbot', icon: MessageSquare, section: null, superAdminOnly: false, supportAllowed: false },
      ],
    },
    {
      label: 'Systeem',
      items: [
        { label: 'Cronjob Monitor', path: '/admin/cronjob-monitor', icon: Activity, section: 'cronjob_monitor', superAdminOnly: false, supportAllowed: false },
        { label: 'User Errors', path: '/admin/errors', icon: AlertTriangle, section: 'user_errors', superAdminOnly: false, supportAllowed: false },
        { label: 'Email Debugger', path: '/admin/email-debugger', icon: Bug, section: 'email_debugger', superAdminOnly: false, supportAllowed: false },
        { label: 'Webhooks', path: '/admin/webhooks', icon: Webhook, section: 'webhooks', superAdminOnly: false, supportAllowed: false },
        { label: 'Storage', path: '/admin/storage', icon: HardDrive, section: null, superAdminOnly: false, supportAllowed: false },
        { label: 'Prompt Logs', path: '/admin/prompt-logs', icon: Database, section: null, superAdminOnly: false, supportAllowed: false },
      ],
    },
    {
      label: 'Instellingen',
      items: [
        { label: 'AI Settings', path: '/admin/ai-settings', icon: Brain, section: null, superAdminOnly: false, supportAllowed: false },
        { label: 'Support Users', path: '/admin/support-users', icon: UserCog, section: null, superAdminOnly: true, supportAllowed: false },
        { label: 'API Documentatie', path: '/admin/api-documentation', icon: Code2, section: null, superAdminOnly: false, supportAllowed: false },
        { label: 'Config', path: '/admin/config', icon: Settings, section: null, superAdminOnly: false, supportAllowed: false },
      ],
    },
  ];

  const visibleGroups = navGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => {
        if (item.superAdminOnly && !isSuperAdmin) return false;
        if (isSupportUser && !item.supportAllowed) return false;
        return true;
      }),
    }))
    .filter(group => group.items.length > 0);

  const isActivePath = (path: string) => {
    if (path === '/admin/chatbot') {
      return location.pathname === path ||
             location.pathname.startsWith('/admin/chatbot-');
    }
    return location.pathname === path;
  };

  const getBadgeForSection = (section: string | null) => {
    if (!section) return null;

    const sectionCounts = alertCounts[section as keyof typeof alertCounts];
    if (!sectionCounts) return null;

    // Special handling for cronjob_monitor section
    if (section === 'cronjob_monitor') {
      const cronjobCounts = sectionCounts as typeof alertCounts.cronjob_monitor;

      // Red badge: if there are unresolved alerts
      if (cronjobCounts.total > 0) {
        const severity = cronjobCounts.critical > 0 ? 'critical' : 'warning';
        return { count: cronjobCounts.total, severity };
      }

      // Orange badge: if no alerts but there are disabled cronjobs
      if (cronjobCounts.disabled > 0) {
        return { count: cronjobCounts.disabled, severity: 'info' as const };
      }

      return null;
    }

    // Special handling for user_errors section: badge all open errors, but only
    // turn red when generation errors are involved.
    if (section === 'user_errors') {
      const errorCounts = sectionCounts as typeof alertCounts.user_errors;
      if (errorCounts.total === 0) return null;
      const severity = errorCounts.generation_errors > 0 ? 'critical' : 'warning';
      return { count: errorCounts.total, severity };
    }

    // Default handling for other sections
    if (sectionCounts.total === 0) return null;
    const critical = 'critical' in sectionCounts ? sectionCounts.critical : 0;
    const severity = critical > 0 ? 'critical' : 'warning';
    return { count: sectionCounts.total, severity };
  };

  // Aggregate the badges of all items in a group so the count is visible
  // on the (possibly collapsed) group header too.
  const getGroupBadge = (items: typeof visibleGroups[number]['items']) => {
    let count = 0;
    let severity: string | null = null;
    for (const item of items) {
      const badge = getBadgeForSection(item.section);
      if (!badge) continue;
      count += badge.count;
      if (!severity || SEVERITY_RANK[badge.severity] > SEVERITY_RANK[severity]) {
        severity = badge.severity;
      }
    }
    if (count === 0 || !severity) return null;
    return { count, severity };
  };

  // Make sure the group holding the current page is opened, so the active
  // item is always visible after navigation.
  useEffect(() => {
    const activeGroup = visibleGroups.find(
      group => group.label && group.items.some(item => isActivePath(item.path))
    );
    if (activeGroup?.label) {
      const label = activeGroup.label;
      setOpenGroups(prev => (prev[label] ? prev : { ...prev, [label]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const sidebarWidth = collapsed ? 'w-16' : 'w-56';
  const mainMargin = collapsed ? 'ml-16' : 'ml-56';

  return (
    <div className="min-h-screen bg-white flex">
      <aside className={`${sidebarWidth} bg-gray-50 border-r border-gray-200 flex flex-col fixed h-screen z-30 transition-all duration-200`}>
        <div className={`flex items-center border-b border-gray-200 h-14 flex-shrink-0 ${collapsed ? 'justify-center px-2' : 'px-4 justify-between'}`}>
          {!collapsed && (
            <img
              src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
              alt="Dibbi"
              className="h-7"
            />
          )}
          <button
            onClick={toggleCollapsed}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors flex-shrink-0"
            title={collapsed ? 'Sidebar uitklappen' : 'Sidebar inklappen'}
          >
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {visibleGroups.map((group, groupIdx) => {
            const groupBadge = getGroupBadge(group.items);
            const groupHasActive = group.items.some(item => isActivePath(item.path));
            // Accordion only applies to labelled groups when the sidebar is
            // expanded. In icon-rail mode everything stays visible.
            const isAccordion = !collapsed && !!group.label;
            const isOpen = !isAccordion || !!(group.label && openGroups[group.label]);

            return (
            <div
              key={group.label ?? `group-${groupIdx}`}
              className={groupIdx > 0 ? (collapsed ? 'mt-2 pt-2 border-t border-gray-200' : 'mt-1') : ''}
            >
              {isAccordion && (
                <button
                  onClick={() => toggleGroup(group.label!)}
                  className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                    groupHasActive ? 'text-gray-600' : 'text-gray-400'
                  } hover:text-gray-700 hover:bg-gray-100`}
                >
                  <ChevronRight
                    className={`w-3 h-3 flex-shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                  />
                  <span className="flex-1 text-left truncate">{group.label}</span>
                  {groupBadge && (
                    <AlertBadge
                      count={groupBadge.count}
                      severity={groupBadge.severity as 'critical' | 'warning' | 'info'}
                    />
                  )}
                </button>
              )}
              <div className={`space-y-0.5 ${isAccordion ? 'mt-0.5' : ''} ${isOpen ? '' : 'hidden'}`}>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = isActivePath(item.path);
                  const badge = getBadgeForSection(item.section);

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      title={collapsed ? item.label : undefined}
                      className={`flex items-center rounded-lg transition-all text-sm ${
                        collapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2'
                      } ${
                        isActive
                          ? 'bg-gray-200 text-gray-900 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="relative flex-shrink-0">
                        <Icon className="w-4 h-4" />
                        {collapsed && badge && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" />
                        )}
                      </div>
                      {!collapsed && (
                        <>
                          <span className="flex-1 truncate">{item.label}</span>
                          {item.path === '/admin/dashboard' && (
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              aiEnabled ? 'bg-green-500' : 'bg-red-500'
                            }`} />
                          )}
                          {badge && (
                            <AlertBadge
                              count={badge.count}
                              severity={badge.severity as 'critical' | 'warning'}
                            />
                          )}
                        </>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
            );
          })}
        </nav>

        <div className="border-t border-gray-200 px-2 py-3">
          <button
            onClick={handleLogout}
            title={collapsed ? 'Uitloggen' : undefined}
            className={`flex items-center rounded-lg w-full text-sm text-gray-700 hover:bg-gray-100 transition-all ${
              collapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2'
            }`}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span>Uitloggen</span>}
          </button>
          {!collapsed && (
            <div className="text-[10px] text-gray-400 text-center mt-2 select-none">
              v{__APP_VERSION__} · {import.meta.env.VITE_APP_ENV || (window.location.hostname.includes('test') ? 'staging' : 'live')}
            </div>
          )}
        </div>
      </aside>

      <div className={`flex-1 ${mainMargin} transition-all duration-200`}>
        <AdminAIWarningBanner />
        <main className="p-8 max-w-7xl mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
